import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { SlidersHorizontal } from "lucide-react";

import { getCollectionSettlements, getOutstandingSettlements, getPendingSettlements, getSettlementData } from "src/api/settlements";
import { useAuth } from "src/providers/AuthProvider";
import withPermissionWrapper from "src/hooks/withPermissionWrapper";
import CustomModal from "src/components/CustomModal";
import CustomDateRange from "src/components/CustomDateRange";
import SettlementDetailsModal from "src/components/modals/SettlementDetailsModal";
import SettlementTransactions from "src/components/modals/SettlementTransactions";
import { Button } from "src/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "src/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "src/components/ui/tabs";
import { DataTable, PageHead, StatCard, type Column, type RowsPerPageOption } from "src/components/common";
import { getCustomizationValue, renderCurrency } from "src/lib/utils";
import type { CollectionSettlementRow, OutstandingSettlementRow, PendingSettlementRow } from "src/types/settlement";

const formatDate = (value?: string) => {
	if (!value) return "—";
	const parsed = new Date(value);
	return Number.isNaN(parsed.getTime()) ? "—" : format(parsed, "PPP");
};

/** Shared paging state, read from the URL so a tab switch keeps the page. */
const usePaging = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const page = Number(searchParams.get("page") ?? 1);
	const limitParam = searchParams.get("limit") ?? "10";
	const limit: RowsPerPageOption = limitParam === "All" ? "All" : Number(limitParam);

	const setPage = (next: number) => {
		const params = new URLSearchParams(searchParams);
		params.set("page", String(next));
		setSearchParams(params);
	};

	const setLimit = (next: RowsPerPageOption) => {
		const params = new URLSearchParams(searchParams);
		params.set("limit", String(next));
		params.set("page", "1");
		setSearchParams(params);
	};

	return { page, limit, apiLimit: limit === "All" ? 9999 : limit, setPage, setLimit };
};

const Settlements = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const tab = searchParams.get("tab") ?? "pending";
	const { page, apiLimit } = usePaging();

	const { data } = useQuery({
		queryKey: ["settlements", page, apiLimit],
		queryFn: () => getSettlementData({ page, limit: apiLimit }),
		suspense: true,
	});

	const summary = data?.data;

	const setTab = (next: string) => {
		const params = new URLSearchParams(searchParams);
		params.set("tab", next);
		params.set("page", "1");
		setSearchParams(params);
	};

	return (
		<div className="tw-space-y-5">
			<PageHead title="Settlements" subtitle="Track what has been collected, what is pending, and what is still owed." />

			<div className="tw-grid tw-gap-4 tw-grid-cols-1 sm:tw-grid-cols-2 xl:tw-grid-cols-4">
				<StatCard label="Collection Settlements" value={renderCurrency(summary?.collection_settlement ?? 0)} tone="navy" />
				<StatCard label="Pending Settlements" value={renderCurrency(summary?.pending_settlement ?? 0)} tone="amber" />
				<StatCard label="Paid Amount" value={renderCurrency(summary?.paid_amount ?? 0)} tone="teal" />
				<StatCard label="Outstanding Balance" value={renderCurrency(summary?.outstanding_balance ?? 0)} tone="red" />
			</div>

			<Tabs value={tab} onValueChange={setTab}>
				<div className="tw-mb-4 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
					<TabsList className="tw-flex-wrap !tw-gap-1 !tw-bg-transparent !tw-p-0">
						<TabsTrigger value="pending" className="!tw-bg-white !tw-text-text-soft data-[state=active]:!tw-bg-navy data-[state=active]:!tw-text-white tw-rounded-lg tw-border tw-border-border tw-text-[13px]">
							Pending Settlements
						</TabsTrigger>
						<TabsTrigger value="collection" className="!tw-bg-white !tw-text-text-soft data-[state=active]:!tw-bg-navy data-[state=active]:!tw-text-white tw-rounded-lg tw-border tw-border-border tw-text-[13px]">
							Collection Settlements
						</TabsTrigger>
						<TabsTrigger value="outstanding" className="!tw-bg-white !tw-text-text-soft data-[state=active]:!tw-bg-navy data-[state=active]:!tw-text-white tw-rounded-lg tw-border tw-border-border tw-text-[13px]">
							Outstanding Balance
						</TabsTrigger>
					</TabsList>

					<Popover>
						<PopoverTrigger asChild>
							<Button size="sm" variant="outline" className="tw-gap-1.5" aria-label="Filters">
								<SlidersHorizontal className="tw-h-4 tw-w-4" />
								Filters
							</Button>
						</PopoverTrigger>
						<PopoverContent align="end" className="tw-w-auto tw-p-2">
							<CustomDateRange />
						</PopoverContent>
					</Popover>
				</div>

				<TabsContent value="pending">
					<PendingTab />
				</TabsContent>
				<TabsContent value="collection">
					<CollectionTab />
				</TabsContent>
				<TabsContent value="outstanding">
					<OutstandingTab />
				</TabsContent>
			</Tabs>
		</div>
	);
};

const PendingTab = () => {
	const { user } = useAuth();
	const { page, limit, apiLimit, setPage, setLimit } = usePaging();
	const customerLabel = getCustomizationValue(user, "customer_label", true) || "Customer";

	const { data, isLoading } = useQuery({
		queryKey: ["settlements", "pending", page, apiLimit],
		queryFn: () => getPendingSettlements({ page, limit: apiLimit }),
	});

	const list = data?.data;
	const rows = list?.data ?? [];

	const columns: Column<PendingSettlementRow>[] = [
		{
			key: "customer",
			header: `${customerLabel} Name`,
			primary: true,
			cell: (row) => [row.first_name, row.last_name].filter(Boolean).join(" ") || "—",
		},
		{ key: "business", header: "Business Name", cell: (row) => row.business_name ?? "—" },
		{ key: "method", header: "Mode of Payment", cell: (row) => <span className="tw-capitalize">{row.payment_method ?? "—"}</span> },
		{ key: "date", header: "Transaction Date", cell: (row) => formatDate(row.due_date) },
		{
			key: "amount",
			header: "Amount Paid",
			cell: (row) => <span className="tw-font-semibold">{renderCurrency(row.amount ?? 0)}</span>,
		},
		{
			key: "actions",
			header: "",
			hideOnMobile: true,
			className: "tw-w-[130px] tw-text-right",
			cell: (row) => (
				<CustomModal
					title="Transaction Details"
					size="normal"
					trigger={
						<button
							type="button"
							className="tw-rounded-lg tw-px-2 tw-py-1 tw-text-[12.5px] tw-font-semibold tw-text-teal hover:tw-bg-teal-tint"
						>
							View Receipt
						</button>
					}
				>
					<SettlementDetailsModal settlement={row} />
				</CustomModal>
			),
		},
	];

	return (
		<DataTable
			data={rows}
			columns={columns}
			rowKey={(row, index) => row.uuid ?? index}
			loading={isLoading}
			totalRows={list?.total ?? rows.length}
			page={page}
			onPageChange={setPage}
			rowsPerPage={limit}
			onRowsPerPageChange={setLimit}
			emptyTitle="You're all caught up"
			emptyDescription="No settlements are pending right now."
		/>
	);
};

const CollectionTab = () => {
	const { page, limit, apiLimit, setPage, setLimit } = usePaging();

	const { data, isLoading } = useQuery({
		queryKey: ["settlements", "collection", page, apiLimit],
		queryFn: () => getCollectionSettlements({ page, limit: apiLimit }),
	});

	const list = data?.data;
	const rows = list?.data ?? [];

	const columns: Column<CollectionSettlementRow>[] = [
		{ key: "title", header: "Title", primary: true, cell: (row) => row.title ?? "—" },
		{ key: "bank", header: "Bank Name", cell: (row) => row.bank_name ?? "—" },
		{ key: "account_name", header: "Bank Acct. Name", cell: (row) => row.account_name ?? "—" },
		{ key: "account_number", header: "Bank Acct No.", cell: (row) => row.account_number ?? "—" },
		{
			key: "actions",
			header: "",
			hideOnMobile: true,
			className: "tw-w-[160px] tw-text-right",
			cell: (row) => (
				<CustomModal
					title="Settlement Transactions"
					size="lg"
					trigger={
						<button
							type="button"
							className="tw-rounded-lg tw-px-2 tw-py-1 tw-text-[12.5px] tw-font-semibold tw-text-teal hover:tw-bg-teal-tint"
						>
							View Transactions
						</button>
					}
				>
					<SettlementTransactions uuid={row.uuid} />
				</CustomModal>
			),
		},
	];

	return (
		<DataTable
			data={rows}
			columns={columns}
			rowKey={(row, index) => row.uuid ?? index}
			loading={isLoading}
			totalRows={list?.total ?? rows.length}
			page={page}
			onPageChange={setPage}
			rowsPerPage={limit}
			onRowsPerPageChange={setLimit}
			emptyTitle="No collection settlements"
			emptyDescription="Settlement batches paid into your linked banks will appear here."
		/>
	);
};

const OutstandingTab = () => {
	const { user } = useAuth();
	const { page, limit, apiLimit, setPage, setLimit } = usePaging();
	const navigate = useNavigate();
	const customerLabel = getCustomizationValue(user, "customer_label", true) || "Customer";
	const invoiceLabel = getCustomizationValue(user, "invoice_label", true) || "Invoice";

	const { data, isLoading } = useQuery({
		queryKey: ["settlements", "outstanding", page, apiLimit],
		queryFn: () => getOutstandingSettlements({ page, limit: apiLimit }),
	});

	const list = data?.data;
	const rows = list?.data ?? [];

	const columns: Column<OutstandingSettlementRow>[] = [
		{ key: "invoice", header: `${invoiceLabel} ID`, primary: true, cell: (row) => row.invoice_id ?? "—" },
		{
			key: "customer",
			header: `${customerLabel} Name`,
			cell: (row) => [row.first_name, row.last_name].filter(Boolean).join(" ") || "—",
		},
		{ key: "business", header: "Business Name", cell: (row) => row.business_name ?? "—" },
		{ key: "method", header: "Mode of Payment", cell: (row) => <span className="tw-capitalize">{row.payment_method ?? "—"}</span> },
		{ key: "date", header: "Transaction Date", cell: (row) => formatDate(row.due_date) },
		{ key: "paid", header: "Amount Paid", cell: (row) => renderCurrency(row.amount_paid ?? 0) },
		{
			key: "owed",
			header: "Outstanding",
			cell: (row) => <span className="tw-font-semibold tw-text-red">{renderCurrency(row.amount_owed ?? 0)}</span>,
		},
		{
			key: "actions",
			header: "",
			hideOnMobile: true,
			className: "tw-w-[80px] tw-text-right",
			cell: (row) => (
				<button
					type="button"
					// Invoice detail is a modal on the invoices page now (decision D2).
					onClick={() => navigate(`/invoices?invoice=${row.uuid ?? ""}`)}
					className="tw-rounded-lg tw-px-2 tw-py-1 tw-text-[12.5px] tw-font-semibold tw-text-teal hover:tw-bg-teal-tint"
				>
					View
				</button>
			),
		},
	];

	return (
		<DataTable
			data={rows}
			columns={columns}
			rowKey={(row, index) => row.uuid ?? index}
			loading={isLoading}
			totalRows={list?.total ?? rows.length}
			page={page}
			onPageChange={setPage}
			rowsPerPage={limit}
			onRowsPerPageChange={setLimit}
			emptyTitle="Nothing outstanding"
			emptyDescription="Invoices with an unpaid balance will appear here."
		/>
	);
};

export default withPermissionWrapper(Settlements, "view_settlements");
